import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PageBody } from '../../components/SiteWrapper';
import {
  Button,
  Card,
  Col,
  OverlayTrigger,
  Popover,
  Row,
} from 'react-bootstrap';
import * as MinistryReportApi from '../../api/MinistryReportApi';
import { toast } from 'react-toastify';
import DataTable from '../../components/DataTable';
import { useRowSpan } from '../../components/DataTable/useRowSpan';
import { FormFieldSelect } from '../../components/Form/FormField';
import { Controller, useForm } from 'react-hook-form';
import moment from 'moment';
import {
  IconChevronDown,
  IconChevronUp,
  IconFile3d,
  IconInfoCircle,
  IconPencil,
  IconPlus,
  IconSquareXFilled,
} from '@tabler/icons-react';
import CustomModal from '../../components/CustomModal/CustomModal';
import { useDropzone } from 'react-dropzone';
import * as MinistryExamApi from '../../api/MinistryExams';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import CustomSelect from './CustomSelect';
import FileUploadedStatus from './FileUploadedStatus';
const uploadSchema = yup.object().shape({
  exam: yup.object().required('Exam is required').nullable(),
  incidentFile: yup
    .mixed()
    .required('File is required')
    .test('fileType', 'Only .xlsx files are allowed', (value) =>
      value?.name?.endsWith('.xlsx')
    ),
});

const IncidentListing = () => {
  const [data, setData] = useState([]);
  const [examDays, setExamDays] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [selectOption2Disable, setSelectOption2Disable] = useState(true);
  const [selectOption3Disable, setSelectOption3Disable] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [isAnyCellEditing, setIsAnyCellEditing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const [filter, setFilter] = useState({
    _page: 1,
    _limit: 25,
    q: '',
    _sort: '',
    _order: 'desc',
    shift: '',
    examDay: '',
    selectedExam: '',
  });

  const [ministryExams, setMinistryExams] = useState([]);

  const [uploadedFile, setUploadedFile] = useState(null);

  const handleFileRemove = (field) => {
    setUploadedFile(null);
    resetField('incidentFile');
  };
  const { getRootProps, getInputProps, acceptedFiles, fileRejections } =
    useDropzone({
      onDrop: (acceptedFiles) => {
        const file = acceptedFiles[0];
        if (file) {
          setUploadedFile(file);
          setValue('incidentFile', file);
        }
      },
      accept: {
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
          '.xlsx',
        ],
      },
      multiple: false,
    });
  const DEFAULT_UPLOAD_FILE_MODAL = {
    show: false,
    isLoading: false,
    title: 'Import Incidents',
  };
  const [showModal, setShowModal] = useState(DEFAULT_UPLOAD_FILE_MODAL);
  const onValid = async (formData) => {
    setSubmitting(true);
    const data = new FormData();
    data.append('exam', formData.exam?.value);
    data.append('file', formData.incidentFile);
    data.append('fileFor', formData?.fileFor?.value);

    try {
      const res = await MinistryReportApi.uploadIncidents(data);
      if (res?.success) {
        toast.success(res?.message);
        setShowModal((prev) => ({ ...prev, show: false }));
        resetUploadForm();
        setUploadedFile(null);
        setSubmitting(false);
        getData();
      } else {
        toast.error(`${res?.message}`);
        setSubmitting(false);
      }
    } catch (err) {
      toast.error('Failed to upload file');
      console.error(err);
      setSubmitting(false);
    }
  };

  const onInvalid = async (errors) => {
    Object.values(errors).forEach((error) => {
      if (error?.message) toast.error(error.message);
    });
  };
  const shiftColors = {
    shift1: 'rgb(203,235,174)',
    shift2: 'rgb(179,218,244)',
    shift3: 'rgb(255,224,143)',
  };

  const categoryColors = {
    drivedownload: 'rgb(254, 195, 190)',
    contentdelivery: 'rgb(224,224,224)',
    contentprocess: 'rgb(255,224,143)',
    entryofcandidates: 'rgb(203,235,174)',
    registration: 'rgb(179,218,244)',
    aadharauthentication: 'rgb(187,211,218)',
    examcommencement: 'rgb(223,194,238)',
    allowedcandidateslogin: 'rgb(121,159,229)',
    examcompletion: 'rgb(144,132,219)',
    lockunlockincidents: 'rgb(122,234,203)',
  };

  const {
    control,
    formState: { errors: incidentListing },
    watch,
    setValue,
  } = useForm({
    mode: 'onChange',
  });
  const cellElementWidth = `160px`;

  const {
    handleSubmit,
    control: uploadControl,
    formState: { errors: fileUploadDetailsError },
    watch: watchUpload,
    setValue: setUploadValue,
    reset: resetUploadForm,
    resetField,
    getValues,
  } = useForm({
    resolver: yupResolver(uploadSchema),
    mode: 'onChange',
  });

  const initialState = {
    sortBy: [{ id: 'id', desc: false }],
    pageSize: filter._limit,
    pageIndex: 0,
  };

  const getData = useCallback(() => {
    MinistryReportApi.getAllIncidents(filter)
      .then((res) => {
        setData(res?.data?.data);
        setTotalRecords(res?.data?.totalCount || 0);
        setExamDays(res?.data?.examDays || []);
        setShifts(res?.data?.shifts || []);
      })
      .catch((error) => {
        toast.error(error?.message || 'Failed to fetch data');
      });
  }, [filter]);

  useEffect(() => {
    getData();
  }, [filter]);

  const selectedExamDay = watch('examDay');
  const selectedShift = watch('shift');
  const selectedExam = watchUpload('exam');

  useEffect(() => {
    if (
      selectedExamDay ||
      selectedExamDay != '' ||
      selectedShift ||
      selectedShift != ''
    ) {
      setFilter((prev) => ({
        ...prev,
        shift: selectedShift?.value,
        examDay: selectedExamDay?.value,
      }));
    }
  }, [selectedShift, selectedExamDay]);

  const popoverContent = (
    <Popover>
      <Popover.Body>Click to Edit</Popover.Body>
    </Popover>
  );

  const deleteIncident = (id) => {
    MinistryReportApi.deleteIncident(id)
      .then((res) => {
        toast.success(res?.message);
        getData();
      })
      .catch((error) => {
        toast.error(error?.message || 'Failed to delete incident');
      });
  };

  const columns = (onRemarkChange) => [
    {
      Header: 'Date / Shift',
      accessor: 'date',
      disableSortBy: true,

      Cell: ({ value, row }) => {
        const cellStyle = {
          padding: '12px',
        };
        const normalizedCategory = row.original.shift.toLowerCase();

        const shiftStyles = {
          backgroundColor: shiftColors[normalizedCategory],
          color: 'black',
          fontWeight: 'normal',
          cursor: 'auto',
          margin: '10px 0px 0px 0px',
        };

        console.log(normalizedCategory);

        return (
          <div style={cellStyle}>
            {moment(value).format('DD/MM/YYYY')} <br />
            <Button className='btn-sm' variant='primary' style={shiftStyles}>
              {row.original.shift}
            </Button>
          </div>
        );
      },
    },
    // {
    //   Header: 'Shift',
    //   accessor: 'shift',
    //   disableSortBy: true,
    //   Cell: ({ value }) => {
    //     const normalizedCategory = value.toLowerCase();

    //     const cellStyle = {
    //       backgroundColor: shiftColors[normalizedCategory],
    //       color: 'black',
    //       fontWeight: 'normal',
    //       cursor: 'auto',
    //       margin: '12px',
    //     };
    //     return (
    //       <Button className='btn-sm' variant='primary' style={cellStyle}>
    //         {value}
    //       </Button>
    //     );
    //   },
    // },
    {
      Header: 'Exam',
      accessor: 'exam',
      disableSortBy: true,
      Cell: ({ value }) => {
        const cellStyle = {
          padding: '12px',
          maxHeight: '100px',
          overflow: 'scroll',
        };
        return <div style={cellStyle}>{value}</div>;
      },
    },
    {
      Header: 'LAN',
      disableSortBy: true,
      accessor: 'lan',
      Cell: ({ value }) => {
        const cellStyle = {
          padding: '12px',
        };
        return <div style={cellStyle}>{value}</div>;
      },
    },
    {
      Header: 'Center Name',
      accessor: 'center_name',
      disableSortBy: true,
      Cell: ({ value }) => {
        const cellStyle = {
          padding: '12px',
          maxHeight: '100px',
          overflow: 'scroll',
        };
        return <div style={cellStyle}>{value}</div>;
      },
    },
    {
      Header: 'City(Zone)',
      disableSortBy: true,
      accessor: 'city',
      Cell: ({ row }) => {
        const cellStyle = {
          padding: '12px',
        };
        return (
          <div style={cellStyle}>
            {row.original.city}
            {`(${row.original.zone})`}
          </div>
        );
      },
    },
    // {
    //   Header: 'Zone',
    //   disableSortBy: true,
    //   accessor: 'zone',
    //   Cell: ({ value }) => {
    //     const cellStyle = {
    //       padding: '12px',
    //     };
    //     return <div style={cellStyle}>{value}</div>;
    //   },
    // },
    {
      Header: 'Time of Issue',
      disableSortBy: true,
      accessor: 'time_of_issue',
      Cell: ({ value }) => {
        const cellStyle = {
          padding: '12px',
        };
        return <div style={cellStyle}>{value}</div>;
      },
    },
    {
      disableSortBy: true,
      Header: 'Stages of Exam',
      accessor: 'stages_of_exam',

      Cell: ({ value }) => {
        // Normalize the category name dynamically
        const normalizedCategory = value
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '');
        const cellStyle = {
          backgroundColor: categoryColors[normalizedCategory] || 'transparent',
          cursor: 'auto',
          color: 'black',
          fontWeight: 'normal',
          margin: '12px',
        };
        return (
          <Button className='btn-sm' variant='primary' style={cellStyle}>
            {value}
          </Button>
        );
      },
    },
    {
      Header: (
        <div className='d-flex gap-1'>
          <span>Reason Category</span>
        </div>
      ),
      accessor: 'reason_category',
      disableSortBy: true,
      Cell: ({ value, row }) => {
        const [text, setText] = useState(value || '');

        const handleChange = (e) => {
          const newValue = e.target.value;
          setText(newValue);
          onRemarkChange(row.original.id, 'reason_category', newValue);
        };

        const issueColorMap = {
          Server: 'rgb(26,26,26)',
          Administrative: 'rgb(33,90,108)',
          'Aadhar Service': 'rgb(117,57,0)',
          Node: 'rgb(71,55,33)',
          'Sync Error': 'rgb(36,112,226)',
          'Power/UPS': 'rgb(18,115,75)',
          WLAN: 'rgb(177,23,2)',
          'Internet Issue': 'rgb(228,139,243)',
          LAW: 'rgb(9,83,168)',
          'Cyber Security': 'rgb(90,50,133)',
          Training: 'rgb(32,99,199)',
        };

        return (
          <div
            style={{
              height: '100px',
              minHeight: '100%',
              maxHeight: 'auto',
              width: cellElementWidth,
            }}
            className={`${
              !isAnyCellEditing && `custom-table-cell-color`
            } d-flex justify-content-center align-items-center`}
          >
            <div
              style={{
                height: '100px',
                minHeight: '100%',
                maxHeight: 'auto',
                width: '100%',
                padding: '10px',
              }}
              className='d-flex justify-content-center align-items-center'
            >
              <CustomSelect
                onChange={handleChange}
                options={[
                  { label: '', value: '' },
                  { label: 'Server', value: 'Server' },
                  { label: 'Administrative', value: 'Administrative' },
                  { label: 'Aadhar Service', value: 'Aadhar Service' },
                  { label: 'Node', value: 'Node' },
                  { label: 'Sync Error', value: 'Sync Error' },
                  { label: 'Power/UPS', value: 'Power/UPS' },
                  { label: 'WLAN', value: 'WLAN' },
                  { label: 'Internet Issue', value: 'Internet Issue' },
                  { label: 'LAW', value: 'LAW' },
                  { label: 'Cyber Security', value: 'Cyber Security' },
                  { label: 'Training', value: 'Training' },
                ]}
                value={text}
                backgroundColor={issueColorMap[text] || 'transparent'}
              />
            </div>
          </div>
        );
      },
    },
    {
      Header: (
        <div className='d-flex gap-1'>
          <span>Type Of Issue</span>
        </div>
      ),
      accessor: 'type_of_issue',
      disableSortBy: true,
      Cell: ({ value, row }) => {
        const [isEditing, setIsEditing] = useState(false);
        const [text, setText] = useState(value || '');

        const handleChange = (e) => {
          const newValue = e.target.value;
          setText(newValue);
          onRemarkChange(row.original.id, 'type_of_issue', newValue);
        };

        return (
          <div
            style={{
              height: '100px',
              minHeight: '100%',
              maxHeight: 'auto',
              width: cellElementWidth,
            }}
            className={`${
              !isAnyCellEditing && `custom-table-cell-color`
            } d-flex justify-content-center align-items-center`}
          >
            <div
              style={{
                height: '100px',
                minHeight: '100%',
                maxHeight: 'auto',
                width: '100%',
                padding: '10px',
              }}
              className='d-flex justify-content-center align-items-center'
            >
              <CustomSelect
                onChange={handleChange}
                options={[
                  { label: '', value: '' },
                  { label: 'Minor', value: 'Minor' },
                  { label: 'Major', value: 'Major' },
                ]}
                value={text}
                backgroundColor={
                  text === 'Major'
                    ? 'rgb(255,69,0)'
                    : text === 'Minor'
                    ? 'rgb(12,61,148)'
                    : 'transparent'
                }
              />
            </div>
          </div>
        );
      },
    },
    {
      Header: (
        <div className='d-flex gap-1'>
          <span>Remarks Of ECA</span>
          <OverlayTrigger
            trigger='click'
            rootClose
            placement='right'
            overlay={popoverContent}
          >
            <IconInfoCircle size={'18px'} style={{ color: 'blue' }} />
          </OverlayTrigger>
        </div>
      ),
      accessor: 'remarks_of_eca',
      disableSortBy: true,
      Cell: ({ value, row }) => {
        const [isEditing, setIsEditing] = useState(false);
        const [text, setText] = useState(value || '');

        const handleEdit = () => {
          setIsAnyCellEditing(true);
          setIsEditing(true);
        };
        const handleChange = (e) => setText(e.target.value);
        const handleSave = () => {
          setIsAnyCellEditing(false);
          setIsEditing(false);
          onRemarkChange(row.original.id, 'remarks_of_eca', text);
        };
        const handleKeyPress = (e) => {
          if (e.key === 'Enter') {
            handleSave();
          }
        };

        return (
          <div
            style={{
              height: '100px',
              minHeight: '100%',
              maxHeight: 'auto',
              width: cellElementWidth,
            }}
            className={`${
              !isAnyCellEditing && `custom-table-cell-color`
            } d-flex justify-content-center align-items-center`}
          >
            {isEditing ? (
              <textarea
                value={text}
                onChange={handleChange}
                onBlur={handleSave}
                onKeyDown={handleKeyPress}
                className='form-control'
                style={{
                  fontSize: '16px',
                  height: '100%',
                  zIndex: '999',
                }}
                autoFocus
              />
            ) : (
              <div
                className='d-flex justify-content-between align-items-center'
                style={{
                  width: '100%',
                  height: '100%',
                  padding: '2px 4px',
                }}
                onClick={handleEdit}
              >
                <span
                  style={{
                    flex: 1,
                    overflow: 'scroll',
                    textOverflow: 'ellipsis',
                    height: '100%',
                    width: '100%',
                  }}
                >
                  {text || ''}
                </span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      Header: (
        <div className='d-flex gap-1'>
          <span>Remarks Of RRB</span>
          <OverlayTrigger
            trigger='click'
            rootClose
            placement='right'
            overlay={popoverContent}
          >
            <IconInfoCircle size={'18px'} style={{ color: 'blue' }} />
          </OverlayTrigger>
        </div>
      ),
      accessor: 'remarks_of_rrb',
      disableSortBy: true,
      Cell: ({ value, row }) => {
        const [isEditing, setIsEditing] = useState(false);
        const [text, setText] = useState(value || '');

        const handleEdit = () => {
          setIsAnyCellEditing(true);
          setIsEditing(true);
        };
        const handleChange = (e) => setText(e.target.value);
        const handleSave = () => {
          setIsAnyCellEditing(false);
          setIsEditing(false);
          onRemarkChange(row.original.id, 'remarks_of_rrb', text);
        };
        const handleKeyPress = (e) => {
          if (e.key === 'Enter') {
            handleSave();
          }
        };

        return (
          <div
            style={{
              height: '100px',
              minHeight: '100%',
              maxHeight: 'auto',
              width: cellElementWidth,
            }}
            className={`${
              !isAnyCellEditing && `custom-table-cell-color`
            } d-flex justify-content-center align-items-center`}
          >
            {isEditing ? (
              <textarea
                value={text}
                onChange={handleChange}
                onBlur={handleSave}
                onKeyDown={handleKeyPress}
                className='form-control'
                style={{
                  fontSize: '16px',
                  height: '100%',
                  zIndex: '999',
                }}
                autoFocus
              />
            ) : (
              <div
                className='d-flex justify-content-between align-items-center'
                style={{
                  width: '100%',
                  height: '100%',
                  padding: '2px 4px',
                }}
                onClick={handleEdit}
              >
                <span
                  style={{
                    flex: 1,
                    overflow: 'scroll',
                    textOverflow: 'ellipsis',
                    height: '100%',
                    width: '100%',
                  }}
                >
                  {text || ''}
                </span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      Header: 'Action',
      accessor: 'action',
      disableSortBy: true,
      Cell: ({ row }) => {
        return (
          <div className='ms-3 me-3'>
            <Button
              variant='none'
              className='btn btn-outline-primary bg-light-rrb border btn-sm'
              onClick={() => deleteIncident(row.original.id)}
            >
              Not an Incident
            </Button>
          </div>
        );
      },
    },
  ];

  const handleRemarkChange = async (id, field, newValue) => {
    setData((prevData) =>
      prevData.map((item) =>
        item.id === id ? { ...item, [field]: newValue } : item
      )
    );

    // Optional API call to persist changes
    try {
      const formData = new FormData();
      formData.append('id', id);
      formData.append('field', field);
      formData.append('value', newValue);
      await MinistryReportApi.updateRemarks(formData);
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      toast.error(`Failed to update ${field}`);
      // Optionally revert state on failure
      // setData(prevData);
    }
  };

  const getAllMinistryExams = async () => {
    try {
      const response = await MinistryExamApi.getAll();
      if (response?.success) {
        const exams =
          response?.data?.records?.map(({ exam_name, id }) => ({
            label: exam_name,
            value: id,
          })) || [];

        setMinistryExams(exams);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getAllMinistryExams();
  }, []);

  return (
    <div className='pb-3'>
      <Card className='p-0 shadow-none'>
        <Row>
          <div className='px-3 py-2'>
            <Col md='12'>
              <Row className='d-flex align-items-center'>
                <Col md='6'>
                  <h2 className='page-title ps-2'>Incidents</h2>
                </Col>
                <Col md='6' className='h-100'>
                  <div className='w-100 d-flex justify-content-end pe-2'>
                    <Button
                      variant='primary'
                      className='d-flex gap-2 p-2 m-0'
                      onClick={() => {
                        setShowModal((prev) => ({ ...prev, show: true }));
                      }}
                    >
                      <IconPlus size={20} /> Import
                    </Button>
                  </div>
                </Col>
              </Row>
            </Col>
          </div>
        </Row>
        <hr className='m-0' />

        <Card.Body className='p-0'>
          <DataTable
            cardClass='border-0 shadow-none p-0 m-0'
            columns={columns(handleRemarkChange)}
            data={data}
            totalRecords={totalRecords}
            manual={true}
            tableHooks={(hooks) => {
              hooks.useInstance.push(useRowSpan);
            }}
            enableRowSpan
            defaultPageLength={[25, 50, 100]}
            setFilter={setFilter}
            initialState={initialState}
            align='table-vcenter'
            extraSelectFilter={true}
            extraSelectOptions={ministryExams.map((exam) => ({
              label: exam.label,
              key: exam.value,
            }))}
            setSelectState={(id) => {
              setFilter((prev) => ({
                ...prev,
                selectedExam: id,
                examDay: null,
                shift: null,
              }));
              if (id && id != '') {
                setSelectOption2Disable(false);
                setShifts([]);
              } else {
                setSelectOption2Disable(true);
                setSelectOption3Disable(true);
                setExamDays([]);
                setShifts([]);
              }
            }}
            defaultOption={{ label: 'Select Exam', key: '' }}
            extraSelectFilter2={true}
            extraSelectOptions2={examDays.map((exam) => ({
              label: moment(exam.date).format('DD/MM/YYYY'),
              key: exam.date,
            }))}
            setSelectState2={(id) => {
              setFilter((prev) => ({
                ...prev,
                examDay: id,
                shift: null,
              }));
              if (id && id != '') {
                setSelectOption3Disable(false);
              } else {
                setSelectOption3Disable(true);
              }
              setShifts([]);
            }}
            defaultOption2={{ label: 'Select Exam Day', key: '' }}
            selectOption2Disable={selectOption2Disable}
            extraSelectFilter3={true}
            extraSelectOptions3={shifts.map((s) => ({
              label: s.shift,
              key: s.shift,
            }))}
            setSelectState3={(id) => {
              setFilter((prev) => ({
                ...prev,
                shift: id,
              }));
            }}
            defaultOption3={{ label: 'Select Shift', key: '' }}
            selectOption3Disable={selectOption3Disable}
            customClass='custom-table-padding-zero'
            placeholder={'Search by Exam'}
          />
        </Card.Body>
      </Card>
      {showModal?.show && (
        <CustomModal
          show={showModal?.show}
          modalHeading={showModal?.title}
          size='lg'
          hadBodyPadding
          onHide={() => {
            resetUploadForm();
            handleFileRemove();
            setShowModal((prev) => ({ ...prev, show: false }));
            setShowPreview(false);
          }}
          hasActions={false}
          scrollable
          // centered
        >
          <form onSubmit={handleSubmit(onValid, onInvalid)}>
            <div className='p-3 d-flex flex-column gap-3'>
              <FormFieldSelect
                label={'Exam'}
                name='exam'
                control={uploadControl}
                options={ministryExams?.length > 0 ? ministryExams : []}
                error={fileUploadDetailsError?.exam}
                size='12'
                placeholder='Select Exam'
                required
              />
              {/* <FormFieldSelect
                label={'File for'}
                name='fileFor'
                control={uploadControl}
                options={MONITORING_INCIDENT_STAGES}
                error={fileUploadDetailsError?.fileFor}
                size='12'
                placeholder='Select type'
                required
              /> */}
            </div>
            <Controller
              name='incidentFile'
              control={uploadControl}
              rules={{
                required: 'File is required',
              }}
              render={({ field }) => (
                <div
                  {...getRootProps()}
                  className='dropzone border rounded text-center position-relative p-3'
                  style={{
                    cursor: 'pointer',
                    backgroundColor: '#f8f9fa',
                    border: '2px dashed #aaa',
                    marginBottom: '15px',
                  }}
                >
                  <input
                    {...getInputProps({
                      onChange: (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setUploadedFile(file);
                          field.onChange(file);
                        }
                      },
                    })}
                  />
                  <div className='d-flex flex-column gap-3 justify-content-center align-items-center w-100'>
                    <IconFile3d size={40} stroke={1} color='blue' />
                    {uploadedFile ? (
                      <div className='mt-3 text-center'>
                        <strong>Selected File:</strong> {uploadedFile.name}
                        <span
                          style={{ cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFileRemove();
                          }}
                        >
                          <IconSquareXFilled style={{ color: 'red' }} />
                        </span>
                      </div>
                    ) : (
                      <p>
                        <span
                          style={{ color: 'blue' }}
                          className='custom-fs-16'
                        >
                          Click to upload
                        </span>{' '}
                        or Drag & drop an Excel (.xlsx) file here.
                      </p>
                    )}
                  </div>
                </div>
              )}
            />

            <div className='p-3 d-flex gap-3 justify-content-between'>
              <div className='d-flex gap-3'>
                <Button type='submit' disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit'}
                </Button>
                <Button
                  variant='none'
                  onClick={() => {
                    resetField('exam');
                    // clearErrors('exam'); // optional if you want to remove error state
                    setShowModal((prev) => ({ ...prev, show: false }));
                    setShowPreview(false);
                  }}
                >
                  Cancel
                </Button>
              </div>
              {selectedExam?.value && (
                <div className='d-flex align-items-center justify-content-between'>
                  <span
                    style={{ cursor: 'pointer', color: 'blue' }}
                    onClick={() => setShowPreview((prev) => !prev)}
                    className='d-flex align-items-center gap-2'
                  >
                    <span>Preview Uploaded Files</span>
                    {showPreview ? (
                      <IconChevronUp size={18} />
                    ) : (
                      <IconChevronDown size={18} />
                    )}
                  </span>
                </div>
              )}
            </div>
          </form>

          {showPreview && (
            <div className=''>
              <FileUploadedStatus exam_id={selectedExam} />
            </div>
          )}
        </CustomModal>
      )}
    </div>
  );
};

export default IncidentListing;
